import { useLanguage } from "@utils/hooks/useLanguage";
import { useMemo } from "react";
import { useTranslations } from "../../../../../../translation/TranslationContext";
import { useColorContext } from "@stores/ColorContext";
import { useVersion } from "@utils/hooks/useVersion";
import { T } from "@utils/enums";
import { useTheme } from "@stores/ThemeContext";

interface PreviouseMeetingsLineProps {
    label: string;
    home: number;
    draw: number;
    away: number;
}

const PreviouseMeetingsLine = ({ label, home, draw, away}: PreviouseMeetingsLineProps) => {
    const language = useLanguage();
    const translations = useTranslations(language);
    const { colors } = useColorContext();
    const { isV1, isV2 } = useVersion();
    const { theme } = useTheme();
    const normalizedHome = useMemo(() => Math.max(0, home), [home]);
    const normalizedAway = useMemo(() => Math.max(0, away), [away]);
    const normalizedDraw = useMemo(() => Math.max(0, draw), [draw]);
    
    const homeWidth = useMemo(() => {
        return normalizedHome === 0 && normalizedAway === 0 && normalizedDraw === 0 ? '33.33%' : `${normalizedHome * 100}%`;
    }, [normalizedHome, normalizedAway, normalizedDraw]);

    const awayWidth = useMemo(() => {
        return normalizedHome === 0 && normalizedAway === 0 && normalizedDraw === 0 ? '33.33%' : `${normalizedAway * 100}%`;
    }, [normalizedHome, normalizedAway, normalizedDraw]);

    const drawWidth = useMemo(() => {
        return normalizedHome === 0 && normalizedAway === 0 && normalizedDraw === 0 ? '33.33%' : `${normalizedDraw * 100}%`;
    }, [normalizedHome, normalizedAway, normalizedDraw]);

    return (
      <div className="w-full h-[4px] flex items-center justify-between flex-col">
        <div className="w-full h-[4px] flex flex-row gap-[2px] [&>*:first-child]:rounded-l-full [&>*:last-child]:rounded-r-full">
          {homeWidth !== "0%" && (
            <div
              className={`h-[4px] bg-success110 dark:bg-success090 ${homeWidth === "0%" ? "hidden" : ""}`}
              style={{
                width: homeWidth,
                backgroundColor: isV2
                  ? colors?.homeColor
                  : theme === T.LIGHT
                    ? "#0a6638"
                    : "#52cc8e",
                border: isV2 ? `0.6px solid ${colors?.borderColor}` : "none",
              }}
            />
          )}

          {drawWidth !== "0%" && (
            <div
              className={`h-[4px] ${drawWidth === "0%" ? "hidden" : ""}`}
              style={{
                width: drawWidth,
                backgroundColor: "#CCCCCC",
                border: isV2 ? `0.6px solid ${colors?.borderColor}` : "none",
              }}
            />
          )}

          {awayWidth !== "0%" && (
            <div
              className={`h-[4px] bg-attention110 dark:bg-attention090 ${awayWidth === "0%" ? "hidden" : ""}`}
              style={{
                width: awayWidth,
                backgroundColor: isV2
                  ? colors?.awayColor
                  : theme === T.LIGHT
                    ? "#c35000"
                    : "#ffd952",
                border: isV2 ? `0.6px solid ${colors?.borderColor}` : "none",
              }}
            />
          )}
        </div>
      </div>
    );
}

export default PreviouseMeetingsLine;
